import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { FileVaultApiService } from '../../services/file-vault-api.service';
import {
  WorkflowTask,
  WorkflowStep,
  WorkflowInstance,
  WorkflowDecision,
  WorkflowStepState,
  WorkflowState,
  WorkflowStepResult
} from '../../models/file-vault.models';

export interface TaskActionResult {
  success: boolean;
  action: 'approve' | 'reject' | 'delegate' | 'skip';
  result?: WorkflowStepResult;
  error?: string;
}

@Component({
  selector: 'fv-task-detail',
  standalone: true,
  template: `
    <div class="task-detail-container" *ngIf="task || step">
      <!-- Header -->
      <div class="header">
        <div class="header-content">
          <h2 class="title">{{ currentStepName }}</h2>
          <span class="workflow-badge">{{ currentWorkflowName }}</span>
        </div>
        <div class="step-progress" *ngIf="task">
          Step {{ (task.stepIndex || 0) + 1 }} of {{ task.totalSteps || '?' }}
        </div>
      </div>

      <!-- Entity Info -->
      <div class="section entity-section">
        <h3>Related Entity</h3>
        <div class="entity-info">
          <div class="entity-row">
            <span class="label">Type:</span>
            <span class="entity-type-badge" [ngClass]="getEntityTypeBadgeClass()">
              {{ currentEntityType }}
            </span>
          </div>
          <div class="entity-row" *ngIf="currentEntityNumber">
            <span class="label">ID:</span>
            <a class="entity-link" (click)="onViewEntityClick()">{{ currentEntityNumber }}</a>
          </div>
          <div class="entity-row" *ngIf="currentEntityTitle">
            <span class="label">Title:</span>
            <span class="value">{{ currentEntityTitle }}</span>
          </div>
        </div>
      </div>

      <!-- Step Info -->
      <div class="section">
        <h3>Step Details</h3>
        <div class="details-grid">
          <div class="detail-item" *ngIf="currentAssignedTo || currentAssignedToRole">
            <span class="label">Assigned To:</span>
            <span class="value">
              {{ currentAssignedTo || currentAssignedToRole }}
              <span class="role-tag" *ngIf="currentAssignedToRole && currentAssignedTo">({{ currentAssignedToRole }})</span>
            </span>
          </div>
          <div class="detail-item" *ngIf="currentDueDate">
            <span class="label">Due Date:</span>
            <span class="value" [ngClass]="{ 'overdue': isOverdue, 'due-soon': isDueSoon }">
              {{ currentDueDate | date:'medium' }}
              <span class="overdue-tag" *ngIf="isOverdue">OVERDUE</span>
            </span>
          </div>
          <div class="detail-item" *ngIf="step?.description">
            <span class="label">Description:</span>
            <span class="value">{{ step?.description }}</span>
          </div>
          <div class="detail-item" *ngIf="requiresComments">
            <span class="label">Note:</span>
            <span class="value warning">Comments are required for this step</span>
          </div>
        </div>
      </div>

      <!-- Previous Steps (from workflow instance) -->
      <div class="section" *ngIf="workflowInstance?.steps?.length">
        <h3>Previous Steps</h3>
        <div class="previous-steps">
          <div class="step-item" *ngFor="let s of getPreviousSteps()" [ngClass]="getStepClass(s.state)">
            <div class="step-indicator">
              <span class="step-icon">{{ getStepIcon(s.state) }}</span>
            </div>
            <div class="step-content">
              <div class="step-header">
                <span class="step-name">{{ s.stepName }}</span>
                <span class="step-state-badge" [ngClass]="getStepStateBadgeClass(s.state)">
                  {{ getStepStateLabel(s.state) }}
                </span>
              </div>
              <div class="step-meta" *ngIf="s.completedAt">
                <span>{{ s.completedBy }}</span>
                <span class="separator">|</span>
                <span>{{ s.completedAt | date:'short' }}</span>
              </div>
              <div class="step-comments" *ngIf="s.comments">
                "{{ s.comments }}"
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="actions-section">
        <h3>Take Action</h3>

        <!-- Comments input -->
        <div class="comments-input">
          <label for="comments">Comments {{ requiresComments ? '(required)' : '(optional)' }}</label>
          <textarea
            id="comments"
            kendoTextArea
            [(ngModel)]="comments"
            [rows]="3"
            placeholder="Enter your comments..."
            class="comments-textarea">
          </textarea>
        </div>

        <!-- Action buttons -->
        <div class="action-buttons">
          <button kendoButton
                  [themeColor]="'primary'"
                  (click)="openApproveDialog()"
                  [disabled]="isProcessing || !canApprove"
                  *ngIf="showApprove">
            {{ isProcessing ? 'Processing...' : 'Approve' }}
          </button>
          <button kendoButton
                  [themeColor]="'error'"
                  (click)="openRejectDialog()"
                  [disabled]="isProcessing || !canReject"
                  *ngIf="showReject">
            Reject
          </button>
          <button kendoButton
                  (click)="openDelegateDialog()"
                  [disabled]="isProcessing">
            Delegate
          </button>
          <button kendoButton
                  [look]="'flat'"
                  (click)="openSkipDialog()"
                  [disabled]="isProcessing"
                  *ngIf="canSkip">
            Skip
          </button>
        </div>

        <!-- Error message -->
        <div class="error-message" *ngIf="errorMessage">
          {{ errorMessage }}
        </div>
      </div>

      <!-- Approve Confirmation Dialog -->
      <kendo-dialog *ngIf="showApproveDialog" title="Confirm Approval" (close)="closeDialogs()">
        <p>Are you sure you want to approve this step?</p>
        <div class="dialog-comments" *ngIf="comments">
          <strong>Your comments:</strong>
          <p>{{ comments }}</p>
        </div>
        <kendo-dialog-actions>
          <button kendoButton (click)="closeDialogs()">Cancel</button>
          <button kendoButton [themeColor]="'primary'" (click)="doApprove()">Approve</button>
        </kendo-dialog-actions>
      </kendo-dialog>

      <!-- Reject Dialog -->
      <kendo-dialog *ngIf="showRejectDialog" title="Reject Step" (close)="closeDialogs()">
        <p>Please provide a reason for rejection:</p>
        <textarea
          kendoTextArea
          [(ngModel)]="rejectReason"
          [rows]="3"
          placeholder="Reason for rejection (required)"
          class="dialog-textarea">
        </textarea>
        <kendo-dialog-actions>
          <button kendoButton (click)="closeDialogs()">Cancel</button>
          <button kendoButton [themeColor]="'error'" (click)="doReject()" [disabled]="!rejectReason?.trim()">
            Reject
          </button>
        </kendo-dialog-actions>
      </kendo-dialog>

      <!-- Delegate Dialog -->
      <kendo-dialog *ngIf="showDelegateDialog" title="Delegate Task" (close)="closeDialogs()">
        <p>Enter the username or user ID to delegate this task to:</p>
        <input kendoTextBox
               [(ngModel)]="delegateTo"
               placeholder="Username or user ID"
               class="dialog-input" />
        <div class="dialog-field">
          <label>Reason (optional):</label>
          <textarea
            kendoTextArea
            [(ngModel)]="delegateReason"
            [rows]="2"
            placeholder="Reason for delegation">
          </textarea>
        </div>
        <kendo-dialog-actions>
          <button kendoButton (click)="closeDialogs()">Cancel</button>
          <button kendoButton [themeColor]="'primary'" (click)="doDelegate()" [disabled]="!delegateTo?.trim()">
            Delegate
          </button>
        </kendo-dialog-actions>
      </kendo-dialog>

      <!-- Skip Dialog -->
      <kendo-dialog *ngIf="showSkipDialog" title="Skip Step" (close)="closeDialogs()">
        <p>Are you sure you want to skip this step?</p>
        <div class="dialog-field">
          <label>Reason (optional):</label>
          <textarea
            kendoTextArea
            [(ngModel)]="skipReason"
            [rows]="2"
            placeholder="Reason for skipping">
          </textarea>
        </div>
        <kendo-dialog-actions>
          <button kendoButton (click)="closeDialogs()">Cancel</button>
          <button kendoButton (click)="doSkip()">Skip</button>
        </kendo-dialog-actions>
      </kendo-dialog>
    </div>

    <!-- No task selected state -->
    <div class="no-task" *ngIf="!task && !step">
      <p>Select a task to view details</p>
    </div>
  `,
  styles: [`
    .task-detail-container {
      padding: 16px;
      max-width: 700px;
    }

    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 20px;
      padding-bottom: 16px;
      border-bottom: 1px solid var(--fv-border);
    }

    .header-content {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .title {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      color: var(--fv-text);
    }

    .workflow-badge {
      display: inline-block;
      padding: 2px 8px;
      background: var(--fv-status-info-bg);
      color: var(--fv-status-info-fg);
      border-radius: 4px;
      font-size: 12px;
    }

    .step-progress {
      font-size: 13px;
      color: var(--fv-text-secondary);
      padding: 4px 8px;
      background: var(--fv-surface-hover);
      border-radius: 4px;
    }

    .section {
      margin-bottom: 24px;
    }

    .section h3 {
      margin: 0 0 12px 0;
      font-size: 14px;
      font-weight: 600;
      color: var(--fv-text-secondary);
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .entity-section {
      background: var(--fv-surface-alt);
      padding: 16px;
      border-radius: 8px;
    }

    .entity-info {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .entity-row {
      display: flex;
      gap: 8px;
      align-items: center;
    }

    .entity-type-badge {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
    }

    .entity-type-badge.change { background: var(--fv-status-warning-bg); color: var(--fv-status-warning-fg); }
    .entity-type-badge.part { background: var(--fv-status-info-bg); color: var(--fv-status-info-fg); }
    .entity-type-badge.document { background: var(--fv-status-purple-bg); color: var(--fv-status-purple-fg); }
    .entity-type-badge.partversion { background: var(--fv-status-success-bg); color: var(--fv-status-success-fg); }
    .entity-type-badge.documentversion { background: var(--fv-status-error-bg); color: #c2185b; }

    .entity-link {
      color: var(--fv-text-accent);
      cursor: pointer;
      font-family: monospace;
    }

    .entity-link:hover {
      text-decoration: underline;
    }

    .label {
      font-weight: 500;
      color: var(--fv-text-secondary);
      min-width: 60px;
    }

    .value {
      color: var(--fv-text);
    }

    .details-grid {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .detail-item {
      display: flex;
      gap: 8px;
    }

    .detail-item .label {
      min-width: 100px;
    }

    .overdue {
      color: var(--fv-status-error-fg);
    }

    .due-soon {
      color: var(--fv-status-warning-fg);
    }

    .overdue-tag {
      display: inline-block;
      margin-left: 8px;
      padding: 2px 6px;
      background: var(--fv-status-error-fg);
      color: white;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 600;
    }

    .role-tag {
      color: var(--fv-text-muted);
      font-size: 12px;
    }

    .warning {
      color: var(--fv-status-warning-fg);
      font-style: italic;
    }

    .previous-steps {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .step-item {
      display: flex;
      gap: 12px;
      padding: 12px;
      background: var(--fv-surface-alt);
      border-radius: 8px;
      border-left: 3px solid var(--fv-border);
    }

    .step-item.approved { border-left-color: #4caf50; }
    .step-item.rejected { border-left-color: #f44336; }
    .step-item.skipped { border-left-color: #ff9800; }
    .step-item.delegated { border-left-color: #9c27b0; }

    .step-indicator {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: var(--fv-border);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .step-item.approved .step-indicator { background: var(--fv-status-success-bg); }
    .step-item.rejected .step-indicator { background: var(--fv-status-error-bg); }
    .step-item.skipped .step-indicator { background: var(--fv-status-warning-bg); }

    .step-icon {
      font-size: 16px;
    }

    .step-content {
      flex: 1;
    }

    .step-header {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 4px;
    }

    .step-name {
      font-weight: 500;
    }

    .step-state-badge {
      padding: 2px 6px;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
    }

    .step-state-badge.approved { background: var(--fv-status-success-bg); color: var(--fv-status-success-fg); }
    .step-state-badge.rejected { background: var(--fv-status-error-bg); color: var(--fv-status-error-fg); }
    .step-state-badge.skipped { background: var(--fv-status-warning-bg); color: var(--fv-status-warning-fg); }
    .step-state-badge.delegated { background: var(--fv-status-purple-bg); color: var(--fv-status-purple-fg); }

    .step-meta {
      font-size: 12px;
      color: var(--fv-text-muted);
    }

    .separator {
      margin: 0 6px;
    }

    .step-comments {
      margin-top: 6px;
      font-style: italic;
      color: var(--fv-text-secondary);
      font-size: 13px;
    }

    .actions-section {
      background: var(--fv-surface-hover);
      padding: 20px;
      border-radius: 8px;
      margin-top: 24px;
    }

    .actions-section h3 {
      margin-top: 0;
    }

    .comments-input {
      margin-bottom: 16px;
    }

    .comments-input label {
      display: block;
      margin-bottom: 6px;
      font-weight: 500;
      color: var(--fv-text-secondary);
    }

    .comments-textarea {
      width: 100%;
    }

    .action-buttons {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }

    .error-message {
      margin-top: 12px;
      padding: 8px 12px;
      background: var(--fv-status-error-bg);
      color: var(--fv-status-error-fg);
      border-radius: 4px;
      font-size: 13px;
    }

    .no-task {
      padding: 48px;
      text-align: center;
      color: var(--fv-text-muted);
    }

    .dialog-comments {
      margin-top: 12px;
      padding: 12px;
      background: var(--fv-surface-hover);
      border-radius: 4px;
    }

    .dialog-comments strong {
      display: block;
      margin-bottom: 4px;
      font-size: 12px;
      color: var(--fv-text-secondary);
    }

    .dialog-textarea, .dialog-input {
      width: 100%;
      margin-top: 8px;
    }

    .dialog-field {
      margin-top: 12px;
    }

    .dialog-field label {
      display: block;
      margin-bottom: 4px;
      font-size: 13px;
      color: var(--fv-text-secondary);
    }
  `],
  imports: [CommonModule, FormsModule, ButtonsModule, InputsModule, DialogModule]
})
export class TaskDetailComponent implements OnInit, OnChanges {
  private api = inject(FileVaultApiService);

  /** Task data (from my-tasks list) */
  @Input() task?: WorkflowTask;

  /** Alternatively, provide step ID to load */
  @Input() stepId?: string;

  /** Workflow instance (for showing previous steps) */
  @Input() workflowInstance?: WorkflowInstance;

  /** Emitted when action is completed */
  @Output() actionComplete = new EventEmitter<TaskActionResult>();

  /** Emitted when view entity is clicked */
  @Output() viewEntityClick = new EventEmitter<{ entityType: string; entityId: string }>();

  step?: WorkflowStep;
  isLoading = false;
  isProcessing = false;
  errorMessage = '';

  // Form
  comments = '';

  // Dialogs
  showApproveDialog = false;
  showRejectDialog = false;
  showDelegateDialog = false;
  showSkipDialog = false;

  // Dialog fields
  rejectReason = '';
  delegateTo = '';
  delegateReason = '';
  skipReason = '';

  ngOnInit(): void {
    if (this.stepId && !this.task) {
      this.loadStep();
    }
    if (this.task && !this.workflowInstance) {
      this.loadWorkflowInstance();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['stepId'] && this.stepId) {
      this.loadStep();
    }
    if (changes['task'] && this.task && !this.workflowInstance) {
      this.loadWorkflowInstance();
    }
  }

  async loadStep(): Promise<void> {
    if (!this.stepId) return;

    this.isLoading = true;
    try {
      this.step = await this.api.getWorkflowStep(this.stepId).toPromise();
      if (this.step?.instanceId) {
        this.workflowInstance = await this.api.getWorkflow(this.step.instanceId).toPromise();
      }
    } catch (error) {
      console.error('Failed to load step', error);
    } finally {
      this.isLoading = false;
    }
  }

  async loadWorkflowInstance(): Promise<void> {
    if (!this.task?.instanceId) return;

    try {
      this.workflowInstance = await this.api.getWorkflow(this.task.instanceId).toPromise();
    } catch (error) {
      console.error('Failed to load workflow instance', error);
    }
  }

  // Computed properties
  get currentStepName(): string {
    return this.task?.stepName ?? this.step?.stepName ?? 'Task';
  }

  get currentWorkflowName(): string {
    return this.task?.workflowName ?? this.workflowInstance?.templateName ?? 'Workflow';
  }

  get currentEntityType(): string {
    return this.task?.entityType ?? this.workflowInstance?.entityType ?? '';
  }

  get currentEntityId(): string {
    return this.task?.entityId ?? this.workflowInstance?.entityId ?? '';
  }

  get currentEntityNumber(): string | undefined {
    return this.task?.entityNumber ?? this.workflowInstance?.entityNumber;
  }

  get currentEntityTitle(): string | undefined {
    return this.task?.entityTitle ?? this.workflowInstance?.entityTitle;
  }

  get currentAssignedTo(): string | undefined {
    return this.task?.assignedTo ?? this.step?.assignedTo;
  }

  get currentAssignedToRole(): string | undefined {
    return this.task?.assignedToRole ?? this.step?.assignedToRole;
  }

  get currentDueDate(): Date | undefined {
    const date = this.task?.dueDate ?? this.step?.dueDate;
    return date ? new Date(date) : undefined;
  }

  get currentStepId(): string {
    return this.task?.stepId ?? this.step?.id ?? '';
  }

  get isOverdue(): boolean {
    if (this.task?.isOverdue) return true;
    if (!this.currentDueDate) return false;
    return this.currentDueDate < new Date();
  }

  get isDueSoon(): boolean {
    if (this.isOverdue || !this.currentDueDate) return false;
    const diff = this.currentDueDate.getTime() - new Date().getTime();
    return diff < 2 * 24 * 60 * 60 * 1000; // 2 days
  }

  get requiresComments(): boolean {
    return this.task?.requiresComments ?? this.step?.requiresComments ?? false;
  }

  get canSkip(): boolean {
    return this.task?.canSkip ?? this.step?.canSkip ?? false;
  }

  get canApprove(): boolean {
    if (this.requiresComments && !this.comments.trim()) return false;
    if (this.task?.allowedDecisions) {
      return this.task.allowedDecisions.includes(WorkflowDecision.Approve);
    }
    return true;
  }

  get canReject(): boolean {
    if (this.task?.allowedDecisions) {
      return this.task.allowedDecisions.includes(WorkflowDecision.Reject);
    }
    return true;
  }

  get showApprove(): boolean {
    return !this.task?.allowedDecisions || this.task.allowedDecisions.includes(WorkflowDecision.Approve);
  }

  get showReject(): boolean {
    return !this.task?.allowedDecisions || this.task.allowedDecisions.includes(WorkflowDecision.Reject);
  }

  getPreviousSteps(): WorkflowStep[] {
    if (!this.workflowInstance?.steps) return [];
    const currentIndex = this.task?.stepIndex ?? this.step?.stepIndex ?? 0;
    return this.workflowInstance.steps
      .filter(s => s.stepIndex < currentIndex && s.state !== WorkflowStepState.Pending)
      .sort((a, b) => a.stepIndex - b.stepIndex);
  }

  // Dialog methods
  openApproveDialog(): void {
    this.showApproveDialog = true;
  }

  openRejectDialog(): void {
    this.rejectReason = '';
    this.showRejectDialog = true;
  }

  openDelegateDialog(): void {
    this.delegateTo = '';
    this.delegateReason = '';
    this.showDelegateDialog = true;
  }

  openSkipDialog(): void {
    this.skipReason = '';
    this.showSkipDialog = true;
  }

  closeDialogs(): void {
    this.showApproveDialog = false;
    this.showRejectDialog = false;
    this.showDelegateDialog = false;
    this.showSkipDialog = false;
  }

  // Action methods
  async doApprove(): Promise<void> {
    this.closeDialogs();
    this.isProcessing = true;
    this.errorMessage = '';

    try {
      const result = await this.api.approveStep(this.currentStepId, {
        comments: this.comments || undefined
      }).toPromise();

      this.actionComplete.emit({
        success: result?.success ?? true,
        action: 'approve',
        result
      });

      this.comments = '';
    } catch (error: any) {
      this.errorMessage = error.error?.message || error.message || 'Failed to approve step';
      this.actionComplete.emit({
        success: false,
        action: 'approve',
        error: this.errorMessage
      });
    } finally {
      this.isProcessing = false;
    }
  }

  async doReject(): Promise<void> {
    if (!this.rejectReason.trim()) return;

    this.closeDialogs();
    this.isProcessing = true;
    this.errorMessage = '';

    try {
      const result = await this.api.rejectStep(this.currentStepId, {
        reason: this.rejectReason
      }).toPromise();

      this.actionComplete.emit({
        success: result?.success ?? true,
        action: 'reject',
        result
      });

      this.comments = '';
      this.rejectReason = '';
    } catch (error: any) {
      this.errorMessage = error.error?.message || error.message || 'Failed to reject step';
      this.actionComplete.emit({
        success: false,
        action: 'reject',
        error: this.errorMessage
      });
    } finally {
      this.isProcessing = false;
    }
  }

  async doDelegate(): Promise<void> {
    if (!this.delegateTo.trim()) return;

    this.closeDialogs();
    this.isProcessing = true;
    this.errorMessage = '';

    try {
      const result = await this.api.delegateStep(this.currentStepId, {
        delegateTo: this.delegateTo,
        reason: this.delegateReason || undefined
      }).toPromise();

      this.actionComplete.emit({
        success: result?.success ?? true,
        action: 'delegate',
        result
      });

      this.delegateTo = '';
      this.delegateReason = '';
    } catch (error: any) {
      this.errorMessage = error.error?.message || error.message || 'Failed to delegate step';
      this.actionComplete.emit({
        success: false,
        action: 'delegate',
        error: this.errorMessage
      });
    } finally {
      this.isProcessing = false;
    }
  }

  async doSkip(): Promise<void> {
    this.closeDialogs();
    this.isProcessing = true;
    this.errorMessage = '';

    try {
      const result = await this.api.skipStep(this.currentStepId, {
        reason: this.skipReason || undefined
      }).toPromise();

      this.actionComplete.emit({
        success: result?.success ?? true,
        action: 'skip',
        result
      });

      this.skipReason = '';
    } catch (error: any) {
      this.errorMessage = error.error?.message || error.message || 'Failed to skip step';
      this.actionComplete.emit({
        success: false,
        action: 'skip',
        error: this.errorMessage
      });
    } finally {
      this.isProcessing = false;
    }
  }

  onViewEntityClick(): void {
    this.viewEntityClick.emit({
      entityType: this.currentEntityType,
      entityId: this.currentEntityId
    });
  }

  // Helper methods
  getEntityTypeBadgeClass(): string {
    return this.currentEntityType?.toLowerCase() ?? '';
  }

  getStepClass(state: WorkflowStepState): string {
    switch (state) {
      case WorkflowStepState.Approved: return 'approved';
      case WorkflowStepState.Rejected: return 'rejected';
      case WorkflowStepState.Skipped: return 'skipped';
      case WorkflowStepState.Delegated: return 'delegated';
      default: return '';
    }
  }

  getStepIcon(state: WorkflowStepState): string {
    switch (state) {
      case WorkflowStepState.Approved: return '\u2713'; // checkmark
      case WorkflowStepState.Rejected: return '\u2717'; // x
      case WorkflowStepState.Skipped: return '\u21B7'; // arrow
      case WorkflowStepState.Delegated: return '\u21C4'; // arrows
      default: return '\u2022';
    }
  }

  getStepStateBadgeClass(state: WorkflowStepState): string {
    switch (state) {
      case WorkflowStepState.Approved: return 'approved';
      case WorkflowStepState.Rejected: return 'rejected';
      case WorkflowStepState.Skipped: return 'skipped';
      case WorkflowStepState.Delegated: return 'delegated';
      default: return '';
    }
  }

  getStepStateLabel(state: WorkflowStepState): string {
    switch (state) {
      case WorkflowStepState.Pending: return 'Pending';
      case WorkflowStepState.InProgress: return 'In Progress';
      case WorkflowStepState.Approved: return 'Approved';
      case WorkflowStepState.Rejected: return 'Rejected';
      case WorkflowStepState.Skipped: return 'Skipped';
      case WorkflowStepState.Delegated: return 'Delegated';
      default: return 'Unknown';
    }
  }
}
